// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Typography, CircularProgress } from '@mui/material';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EmptyStateCard from 'Components/EmptyStateCard';
import { Box, useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'Hooks';

import {
  closestCenter, CollisionDetection, DndContext, DragEndEvent, DragOverlay, DragStartEvent, rectIntersection,
} from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { moveProjectToFolderThunk, reorderFoldersThunk } from 'Features/folderList/folderListThunk';
import moveFolder from 'Features/folderList/folderOrder';
import { ClearSelectedFolderAction, SetFoldersAction } from 'Features/folderList/folderListActions';
import DragPreview from '../folderList/FolderListItem/DragPreview';
import { useDashboardDragSensors } from './dragSensors';
import PageToolbar from './PageToolbar';
import ProjectsSubheader from './ProjectsSubheader';
import { describeProjectsNotFound } from './projectsNotFound';
import FolderListItem from '../folderList/FolderListItem';
import stylesDef from './styles';
import { AssignProjectToFolderAction } from './projectListActions';
import useDashboardProjects from './useDashboardProjects';
import { useMapViewToggle } from './useMapViewToggle';
import { GetMapCoordinatesThunk } from './projectListThunk';
import { matchesFolderKeyword } from './filterProjects';
import { useDashboardFolders } from './useDashboardFolders';
import { useMountedInChunks } from './useMountedInChunks';
import { useRevealSelectedProject } from './useRevealSelectedProject';
import { sortProjects } from './sortProjects';
import MapRail from './MapRail';
import ProjectListItem from './ProjectListItem';
import ProjectListRow from './ProjectListRow';
import TermsAndConditionsDialog from './Terms&ConditionsDialog';
import { findFolderById, selectFolderList, selectSelectedFolder } from '../folderList/folderListSelector';
import {
  selectAllCoordinates, selectEffectiveOrderSortFilter, selectFilteredCoordinates, selectScopedMissingLocationCount,
} from './projectListSelector';

// Lazy: the ArcGIS SDK (and its theme CSS) is one of the heaviest imports in the app, and
// most dashboard visits never open map view - it loads on the first map-view open instead.
const EsriDashboardMap = lazy(() => import('Features/map/EsriDashboardMap'));

type EmptyState = 'no-projects' | 'not-found' | null;

// The grid's three columns. Windowing needs to know how many cards make a row; the width itself
// stays in styles.ts, where the layout lives.
// Cards or rows mounted per step: the first chunk renders at once, the rest as the user scrolls.
const PROJECT_CHUNK = 30;

// Folder drags snap to the nearest slot so the preview gap opens as the card approaches it; a
// project has to be over a folder before it is filed into it.
const collisionDetection: CollisionDetection = (args) => (
  args.active.data.current?.type === 'folder' ? closestCenter(args) : rectIntersection(args)
);

const ProjectList = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const {
    keywordFilter, sortFilter, showMapview, projectViewMode, mapPickToReveal,
    mapCoordinatesLoading, mapCoordinatesFailed, mapCoordinatesKey,
  } = useAppSelector(
    (state) => state.projectList,
  );
  // The complete set as the current filter leaves it - the server applies the status filters,
  // the client the location one.
  const projectSet = useAppSelector(selectFilteredCoordinates);
  // What the map plots: the set narrowed to the folder it is scoped to, if any (AUT-6432), less
  // the projects with no location. The grid below renders the whole set - the dashboard is
  // every project.
  const plottedCoordinates = useAppSelector(selectAllCoordinates);
  const mapMissingLocationCount = useAppSelector(selectScopedMissingLocationCount);
  // Rows or cards (AUT-6432). Both render through the same window; a row takes the full width.
  const showCards = projectViewMode === 'cards';
  const effectiveOrderSortFilter = useAppSelector(selectEffectiveOrderSortFilter);
  const styles = stylesDef(theme, showMapview);
  const folderList = useAppSelector(selectFolderList);
  const { html } = useAppSelector(
    (state) => state.auth,
  );
  const selectedFolder = useAppSelector(selectSelectedFolder);

  // One fetch behind the grid, the pins, and the rail - see useDashboardProjects.
  useDashboardProjects();

  // Sorting is a client-side comparison over the complete set, so the dropdown re-orders what is
  // already loaded instead of re-running the query a page at a time.
  const projects = useMemo(() => sortProjects(projectSet, sortFilter, keywordFilter), [projectSet, sortFilter, keywordFilter]);

  // The whole set is loaded either way - this paces rendering, never a request - but an
  // organisation with a thousand projects would otherwise commit a thousand cards, drag handles
  // and observers in one go.
  const gridRef = useRef<HTMLDivElement>(null);
  const mounted = useMountedInChunks({ itemCount: projects.length, chunk: PROJECT_CHUNK });
  // Closing the map lands on the project whose pin was picked (AUT-6432).
  useRevealSelectedProject({ listRef: gridRef, projects, mounted });

  useEffect(() => {
    if (selectedFolder?._id) {
      dispatch(ClearSelectedFolderAction());
    }
  }, [selectedFolder]);

  // The organisation's folders, for the Folders section and the cards' badges; narrowing them
  // to search matches is visibleFolders' job below.
  const { foldersAnswered } = useDashboardFolders();

  // The Projects / Map switch in the toolbar; the map rail carries the same switch.
  const { showMap, showProjects } = useMapViewToggle();

  const handleMoveProjectToFolderSuccess = async (project?: IProject, folderId?: string) => {
    if (!project?._id) return;
    await dispatch(AssignProjectToFolderAction({ projectId: project._id, folderId }));
  };

  const isSearching = keywordFilter.length > 0;
  const projectsCount = projects.length;
  const resultsCaption = `${projectsCount} ${projectsCount === 1 ? 'result' : 'results'} for “${keywordFilter}”`;
  const notFoundMessage = describeProjectsNotFound(effectiveOrderSortFilter, keywordFilter);

  // While searching, folderList mixes two kinds of folders in one array: folders whose name
  // matched the keyword (genuine search results) and folders that merely contain a matched
  // project (present only so the project rows' folder badge can resolve them). Only the former
  // render as folder results; the filter mirrors the server's case-insensitive substring match
  // on folder name (folder_search.js), applied client-side because the server response does not
  // distinguish the two kinds.
  const visibleFolders = isSearching
    ? folderList.filter((folder: IFolder) => matchesFolderKeyword(folder, keywordFilter))
    : folderList;

  // Zero search matches must still say "not found", not the no-projects-in-account message -
  // the set holds only what matched, so it can legitimately be empty for an account that has
  // other projects. A single derivation (rather than two independently computed,
  // only-by-convention-exclusive booleans) guarantees exactly one empty state renders, and both
  // share the same in-flight-refetch guard. A folder-name match is a search result, so it
  // suppresses the not-found message even when no project matched.
  //
  // Nothing is claimed before a set has actually been answered: the fetch starts as this mounts,
  // so an empty store on the first render means not yet, not none. The key is stamped only by a
  // completed fetch, which makes it the signal for that.
  const hasAnsweredOnce = Boolean(mapCoordinatesKey);
  const showFoldersSection = visibleFolders.length > 0 || (!isSearching && foldersAnswered);
  const emptyState: EmptyState = (() => {
    if (mapCoordinatesLoading || !hasAnsweredOnce || projectsCount > 0) return null;
    if (!isSearching) return 'no-projects';
    if (visibleFolders.length > 0) return null;
    return 'not-found';
  })();
  const wrapperStyle : {} = showMapview ? styles.contentWrapperMap : styles.contentWrapperGrid;
  // A failed fetch is not an empty account; it gets its own message and a retry.
  const showFetchFailure = mapCoordinatesFailed && !mapCoordinatesLoading;
  const showMapEmptyState = showMapview && !mapCoordinatesLoading && !mapCoordinatesFailed && plottedCoordinates.length === 0;

  // The folder being dragged, drawn by the drag overlay while its in-flow card is the placeholder
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const activeFolder = findFolderById(folderList, activeFolderId ?? undefined);
  const sensors = useDashboardDragSensors();
  // One drag context serves two drags: a folder dropped on another folder reorders the folders
  // (shown at once, then saved for the organization), a project dropped on a folder is filed into it.
  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveFolderId(active.data.current?.type === 'folder' ? String(active.id) : null);
  };

  const handleDragCancel = () => setActiveFolderId(null);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveFolderId(null);
    if (!active.id || !over?.id) return;

    if (active.data.current?.type === 'folder') {
      const moved = moveFolder(folderList, String(active.id), String(over.id));
      if (moved !== folderList) {
        dispatch(SetFoldersAction(moved));
        await dispatch(reorderFoldersThunk(moved.map((folder: IFolder) => folder._id)));
      }
      return;
    }

    try {
      const response = await dispatch(moveProjectToFolderThunk(String(active.id), String(over.id)));
      if (response.success) {
        handleMoveProjectToFolderSuccess(response.data as IProject, String(over.id));
      }
    } catch (error) {
      // Nothing moved - the thunk throws before any local change - but the drop looks as if it
      // did nothing, so the failure is at least on record.
      console.error('Moving the project to the folder failed', error);
    }
  };

  // Map view swaps the folder/list content area for the rail and the map; the rail's Projects /
  // Map switch is the way back. Every project matching the current search/filter that has a
  // location is plotted; the ones that don't are counted rather than silently dropped.
  if (showMapview) {
    return (
      <Box sx={wrapperStyle}>
        <Box sx={styles.mapviewContent}>
          {html !== undefined && html !== 'undefined' && html !== 'null' && html !== null && (<TermsAndConditionsDialog />)}
          <Box sx={styles.mapviewBody}>
            <MapRail />
            <Box sx={styles.mapArea}>
              <Suspense fallback={<Box sx={styles.mapArea} />}>
                <EsriDashboardMap />
              </Suspense>
              {showMapEmptyState && (
                <Box sx={styles.mapEmptyState}>
                  <Typography variant="h3">No projects with a location to show</Typography>
                  <Typography variant="body1">Projects appear here once they have a mapped site.</Typography>
                </Box>
              )}
              {mapMissingLocationCount > 0 && (
                <Box sx={styles.mapMissingLocation}>
                  {`${mapMissingLocationCount} ${mapMissingLocationCount === 1 ? 'project' : 'projects'} without location`}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={wrapperStyle}>
      <Box sx={styles.dashboardContent}>
        {html !== undefined && html !== 'undefined' && html !== 'null' && html !== null && (<TermsAndConditionsDialog />)}
        <Box sx={styles.mapviewLayoutLeft}>
          <PageToolbar
            showMapview={showMapview}
            showMapviewClick={showMap}
            showGridviewClick={showProjects}
          />
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <Box sx={styles.gridListWrapper}>
              {/* Browsing, the Folders heading stands whether or not any exist yet - an empty
                  account is told how to start one rather than shown nothing (AUT-6432).
                  Searching is different: no folder matched is not "you have no folders", so
                  the section stays hidden rather than claiming otherwise. */}
              {showFoldersSection && (
                <>
                  <Typography variant="h5" sx={styles.listTitle}>
                    Folders
                  </Typography>
                  {visibleFolders.length > 0 ? (
                    <SortableContext items={visibleFolders.map((folder: IFolder) => folder._id)} strategy={rectSortingStrategy}>
                      <Box sx={styles.gridList}>
                        {visibleFolders.map((folder: IFolder) => (
                          <FolderListItem
                            showMapview={showMapview}
                            folder={folder}
                            reorderable={!isSearching}
                            key={folder._id}
                          />
                        ))}
                      </Box>
                    </SortableContext>
                  ) : (
                    <EmptyStateCard
                      icon={<FolderOpenOutlinedIcon />}
                      title="You currently do not have any folders."
                      description="Create folders to organize your projects."
                    />
                  )}
                </>
              )}
              {/* Hidden while a fetch is in flight rather than left showing the previous
                  search's cards under the new one's heading - the section title and the count
                  update the moment the dropdown does. While searching, the results caption is
                  the title and the controls stay, so the search can be narrowed and re-sorted. */}
              {!mapCoordinatesLoading && projectsCount > 0 && (
                <>
                  <ProjectsSubheader title={isSearching ? resultsCaption : undefined} />
                  <Box sx={showCards ? styles.gridList : styles.rowList} ref={gridRef}>
                    {projects.slice(0, mounted.mountedCount).map((project: IProjectMapCoordinate) => (showCards ? (
                      <ProjectListItem
                        project={project}
                        key={project._id}
                        handleMoveProjectToFolderSuccess={handleMoveProjectToFolderSuccess}
                        isSelected={project._id === mapPickToReveal}
                      />
                    ) : (
                      <ProjectListRow
                        project={project}
                        key={project._id}
                        isInsideFolder={false}
                        handleMoveProjectToFolderSuccess={handleMoveProjectToFolderSuccess}
                        isSelected={project._id === mapPickToReveal}
                      />
                    )))}
                  </Box>
                  {/* Outside the grid element: inside it the sentinel would take a cell. */}
                  {mounted.mountedCount < projects.length && <Box ref={mounted.sentinelRef} />}
                </>
              )}
              {mapCoordinatesLoading && (
                <Box sx={styles.loading}>
                  <CircularProgress size={50} />
                </Box>
              )}
            </Box>
            <DragOverlay>
              {activeFolder ? <DragPreview folder={activeFolder} /> : null}
            </DragOverlay>
          </DndContext>
          {showFetchFailure && (
            <Box sx={styles.noProjects}>
              <Typography variant="h1">We couldn’t load your projects.</Typography>
              <Button variant="outlined" sx={styles.retryButton} onClick={() => dispatch(GetMapCoordinatesThunk())}>
                Try again
              </Button>
            </Box>
          )}
          {/* An account with no projects still gets the section header and its controls, then
              the card explaining how to start one (AUT-6432). */}
          {emptyState === 'no-projects' && (
            <Box sx={styles.emptyProjects}>
              <ProjectsSubheader />
              <EmptyStateCard
                icon={<PlaceOutlinedIcon />}
                title="You do not have any projects."
                description="Create a new project to get started!"
              />
            </Box>
          )}
          {
            emptyState === 'not-found' && (
              <Box sx={styles.noProjects}>
                <Typography variant="h1">{notFoundMessage}</Typography>
              </Box>
            )
          }
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectList;
